
import React from 'react';
import { motion } from 'framer-motion';
import { Search, Calculator, FileCheck, Home, CheckCircle } from 'lucide-react';
import { fadeIn, staggerContainer, getReducedMotionVariants } from '../utils/motion';
import { Card, CardContent } from '@/components/ui/card';

interface JourneyFlowPageProps {
  reducedMotion: boolean;
}

const JourneyFlowPage: React.FC<JourneyFlowPageProps> = ({ reducedMotion }) => {
  const journeySteps = [
    {
      icon: Calculator,
      title: "Input Your Details",
      description: "Enter your financial information securely"
    },
    {
      icon: Search,
      title: "Search Homes",
      description: "Browse properties within your budget"
    },
    {
      icon: FileCheck,
      title: "Get Pre-Approved",
      description: "Secure financing with confidence"
    },
    {
      icon: Home,
      title: "Make Offers",
      description: "Submit winning offers backed by data"
    },
    {
      icon: CheckCircle,
      title: "Close With Clarity",
      description: "Navigate closing with no surprises"
    }
  ];

  return (
    <div className="space-y-10">
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-center space-y-2 mb-8"
      >
        <h2 className="text-3xl font-bold">Your Homebuying Journey</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          EquiForge guides you through every step of the homebuying process with clarity and confidence.
        </p>
      </motion.div>

      <motion.div
        variants={getReducedMotionVariants(staggerContainer(0.15, 0.2), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6"
      >
        {journeySteps.map((step, index) => (
          <motion.div
            key={step.title}
            variants={getReducedMotionVariants(fadeIn('up', index * 0.1, 0.5), reducedMotion)}
            whileHover={!reducedMotion ? { y: -5, transition: { type: "spring", stiffness: 300 } } : {}}
            className="flex flex-col"
          >
            <Card className="h-full flex flex-col">
              <CardContent className="flex flex-col items-center text-center p-4 space-y-4 flex-grow">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mt-4">
                  <step.icon className="w-8 h-8 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0.5, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="mt-8 text-center"
      >
        <p className="text-muted-foreground italic">
          "EquiForge transforms the traditional homebuying process into a streamlined, data-driven experience."
        </p>
      </motion.div>
    </div>
  );
};

export default JourneyFlowPage;
